import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as More } from '../../assets/SVG/close-circle-greay.svg';
import { Autocomplete, Box, TextField, Button, Typography, } from '@mui/material';
import { RequiredLabel } from '../../utils/commonUtils';
import { initialValues, validationSchema,putTerminateCycle, TerminateCycleData, getTerminateReason,  TerminationCategory } from './TerminateCycleConfirmationHelper';
import { ConfirmationModal } from '../confirmationModal';
import { Formik, Form } from 'formik';
import styles from './CycleOverViewList.module.css';
import { theme } from '../../components/Medical_History_Forms/themeprovider';
import { ThemeProvider } from '@emotion/react';
import { useLoader } from '../loaderProvider/loader';
import { useAlert } from '../alertProvider';


interface TerminateCycleConfirmationCycleProps {
  onClose: () => void;
  cycleId: string | null;
}

const TerminateCycleConfirmation: React.FC<TerminateCycleConfirmationCycleProps> = ({ onClose ,cycleId }) => {
  const { t } = useTranslation('cycleOverview_config');
  const [openConfirmDialog, setOpenConfirmDialog] = React.useState(false);
  const { showLoader, hideLoader } = useLoader(); 
  const { addAlert } = useAlert(); 
  const [formValues, setFormValues] = React.useState<TerminateCycleData>(initialValues);
  const [reasons, setReasons] = React.useState<TerminationCategory[]>([]);
  // const { patientId } = usePatientContext();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleOpenModal = async (values: any, validateForm: () => Promise<Record<string, string>>) => {
    const validationErrors = await validateForm();
    const isValid = Object.keys(validationErrors).length === 0;

    if (isValid) {
      setFormValues(values);  
      setOpenConfirmDialog(true);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchedReasons = await getTerminateReason();
        setReasons(fetchedReasons);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching termination reasons:', error);
      }
    };

    fetchData();
  }, []);

  const handleSubmit = () => {
    showLoader();
    const numericCycleId = cycleId !== null ? Number(cycleId) : 0;
    const payload = {
      ...formValues,
      id: numericCycleId, 
      status:'TERMINATED'
    };
    putTerminateCycle(numericCycleId, payload)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .then((response) => {
        setOpenConfirmDialog(false);  
        addAlert(t('TerminateCycleSuccess'), 'success');
        onClose();  
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error submitting data:', error);
        const errors = error.response?.data?.errors;
        if (Array.isArray(errors) && errors.length > 0) {
          errors.forEach((e: { detail?: string }) => {
            if (e.detail) {
              addAlert(`${t('TerminateCycleError')} ${e.detail}`, 'error');
            }
          });
        } else {
          addAlert(t('TerminateCycleError'), 'error');
        }
      })
      .finally(() => {
        hideLoader();
      });
  };
  

  return (
    <ThemeProvider theme={{ theme }}>
      <div className={'mt-4 p-0'}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" px={3}>
          <Box>
            <Typography variant="h6" className={styles.titleText}>
              {t('TerminateCycle')}
            </Typography>
            <Typography className={styles.spantext} sx={{ mt: 1 }}>
              {t('TerminateCycleConfirm')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <More onClick={onClose} style={{ cursor: 'pointer' }} />
          </Box>
        </Box>
        <hr />

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values, { validateForm }) => handleOpenModal(values, validateForm)}  
        >
          {({ values, handleChange, setFieldValue, touched, errors, }) => (
            <Form>
              <Box display="flex" flexDirection="column" gap="1rem" padding="1rem">
                <Autocomplete
                  options={reasons.map((reason) => ({ label: reason.name_label, value: reason.name }))}
                  getOptionLabel={(option) => option.label ?? ''}
                  value={
                    reasons.length && values.cancel_reason
                      ? reasons
                        .filter((reason) => reason.name === values.cancel_reason)
                        .map((reason) => ({ label: reason.name_label, value: reason.name }))[0] || null
                      : null
                  }
                  onChange={(_, value) => setFieldValue('cancel_reason', value?.value || '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={RequiredLabel(t('ReasonCancellationCategory'), true)}
                      variant="outlined"
                      error={Boolean(touched.cancel_reason && errors.cancel_reason)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-focused fieldset': {
                            borderColor: '#0C7DED',
                          },
                        },
                      }}
                    />
                  )}
                />

                <Autocomplete
                  options={
                    (reasons.find(r => r.name === values.cancel_reason)?.termination_reasons || [])
                      .map(reason => ({ label: reason.name_label, value: reason.name }))
                  }
                  getOptionLabel={(option) => option.label ?? ''}
                  value={
                    values.termination_reason && reasons.find(r => r.name === values.cancel_reason)
                      ? (reasons.find(r => r.name === values.cancel_reason)?.termination_reasons || [])
                        .filter(reason => reason.name === values.termination_reason)
                        .map(reason => ({ label: reason.name_label, value: reason.name }))[0] || null
                      : null
                  }
                  onChange={(_, value) => setFieldValue('termination_reason', value?.value || '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={RequiredLabel(t('ReasonToTerminateCycle'), true)}
                      variant="outlined"
                      error={Boolean(touched.termination_reason && errors.termination_reason)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-focused fieldset': {
                            borderColor: '#0C7DED',
                          },
                        },
                      }}
                    />
                  )}
                />

                <TextField
                  label={t('Remark')}
                  variant="outlined"
                  fullWidth
                  multiline
                  maxRows={3}
                  value={values.remark}
                  onChange={handleChange}
                  name="remark"
                  error={Boolean(touched.remark && errors.remark)}
                  helperText={touched.remark && errors.remark}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: '#0C7DED',
                      },
                    },
                  }}
                />

                <Box display="flex" justifyContent="flex-end" gap="1rem">
                  <Button variant="outlined" onClick={onClose} className={styles.cancelButton}>
                    {t('Cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    className={styles.customButton}
                    type="submit"
                  >
                    {t('Save')}
                  </Button>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>

        <ConfirmationModal
          open={openConfirmDialog}
          handleClose={() => setOpenConfirmDialog(false)}
          onConfirm={handleSubmit}  
          title={t('TerminateCycle')}
          contentMessage={t('TerminateCycleConfirm')}
          type={'save'}
        />
      </div>
    </ThemeProvider>
  );
};

export default TerminateCycleConfirmation;
